"use client";

import { mendMarkdown } from "@herm-studio/tool.js";
import { MouseEventHandler, useContext, useEffect, useRef, useState } from "react";
import { tv } from "tailwind-variants";
import { shareContext } from "../../app/share/[...param]/context";
import { PerfectCorner } from "../perfect-corner";
import { RelationResource } from "../relation-resource";
import { RelationTabs } from "../relation-tabs";
import { fileIcon } from "./file-icon";
import { getLang } from "./lang";
import { renderMarkdown, renderQuestion } from "./md";
import { onlineSearchIcon } from "./tab-icon";

export interface ShareRecord {
  id: number;
  type: number;
  question: string;
  answer: string;
  createTime: string;
  artifactsList: { id: number; artifacts: string }[] | null;
  modeId: number;
  contextId: null;
  ttsLanguageTypeId: null;
  ttsType: null;
  requestFile: null;
  responseFiles: [];
  relationId: null;
  originalUrls: string[];
  thumbnailUrls: null;
  attachments: null;
  artifacts: null;
  modelShowName: string;
  modelType: number;
  relationAnswers: {
    answer: string;
    createTime: string;
  }[];
}

interface ShareRecordsProps {
  data: ShareRecord[];
  user: {
    avatar: string;
    name: string;
  };
}

const ProductName = "Juchats";

const style = tv({
  slots: {
    record: "flex mb-[60px]",
    content: "pt-[2px] flex-1 max-w-full pl-[11px] md:pl-[15px] overflow-visible",
    profile: "text-[16px] font-lexend font-[700]",
    model: "text-[12px] dark:text-[#727272] ml-2 text-[#A8AFBF] font-lexend",
    frame: "w-full h-[70vh] mt-[40px]",
  },
})();

const RenderAnswer = ({
  answer,
  modelId,
  relationAnswers,
  artifacts,
  originalUrls,
}: {
  answer: string;
  modelId: number;
  relationAnswers: { answer: string; createTime: string }[];
  artifacts: null | { id: number; artifacts: string }[];
  originalUrls: string[];
}) => {
  const multiple = relationAnswers.length > 1;
  const [index, setIndex] = useState(0);
  const content = relationAnswers[index];
  const [onlineSearch, setOnlineSearch] = useState("");
  const { setArtifact, open } = useContext(shareContext);

  const onClick = (type: "prev" | "next") => {
    if (type === "prev") {
      setIndex((pre) => {
        if (pre === 0) {
          return pre;
        }
        return pre - 1;
      });
    } else {
      setIndex((pre) => {
        if (pre === relationAnswers.length - 1) {
          return pre;
        }
        return pre + 1;
      });
    }
  };

  const { html, relationResource, filePath, code } = renderMarkdown(
    multiple ? content.answer : answer,
    modelId,
    originalUrls,
    artifacts
  );

  const tabs = [
    {
      label: (
        <span className="inline-flex items-center gap-1">
          {onlineSearchIcon} {onlineSearch}
        </span>
      ),
      component: <RelationResource resource={relationResource.searchResult} />,
    },
  ];

  const handleArtifactClick = (id: number) => {
    const target = artifacts?.find((v) => v.id === id);

    if (target) {
      const html = JSON.parse(target.artifacts).html;
      setArtifact({
        code: html,
      });
      open();
    } else {
      console.warn("artifacts not found in window.artifacts");
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const locale = searchParams.get("lang");
    setOnlineSearch(getLang(locale as string).onlineSearch);
  }, []);

  return (
    <>
      {multiple && (
        <span className="inline-flex items-center text-[12px] font-roboto ml-[5px] align-middle">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-[16px] h-[16px] cursor-pointer"
            onClick={() => onClick("prev")}
          >
            <path d="M11.8284 12.0005L14.6569 14.8289L13.2426 16.2431L9 12.0005L13.2426 7.75781L14.6569 9.17203L11.8284 12.0005Z"></path>
          </svg>
          <span>
            &nbsp;{index + 1}&nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;{relationAnswers.length}&nbsp;
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-[16px] h-[16px] cursor-pointer"
            fill="currentColor"
            onClick={() => onClick("next")}
          >
            <path d="M12.1717 12.0005L9.34326 9.17203L10.7575 7.75781L15.0001 12.0005L10.7575 16.2431L9.34326 14.8289L12.1717 12.0005Z"></path>
          </svg>
        </span>
      )}
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          const artifactDOM = target.closest("[data-artifact-id]");
          if (artifactDOM) {
            const id = artifactDOM.getAttribute("data-artifact-id");
            if (id) {
              handleArtifactClick(+id);
            }
          }
        }}
      />
      {relationResource.searchResult.length > 0 && <RelationTabs tabs={tabs} />}
    </>
  );
};

export const ShareRecords = ({ data, user }: ShareRecordsProps) => {
  const records = data
    .sort((a: any, b: any) => Number(a.relationId) - Number(b.relationId))
    .reduce((result: any[], item: any) => {
      if (item.relationId) {
        const relation = result.findIndex((v) => +v.id === +item.relationId);

        // 找不到关联记录
        if (relation === -1 && item.relationId) {
          return result;
        }

        if (relation === -1) {
          result.push({ answer: item.answer, createTime: item.createTime });
        } else {
          result[relation].relationAnswers.push({ answer: item.answer, createTime: item.createTime });
        }
      } else {
        item.relationAnswers = [{ answer: item.answer, createTime: item.createTime }];
        result.push(item);
      }
      return result;
    }, []) as ShareRecord[];

  const timer = useRef<NodeJS.Timeout | null>(null);

  const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement;
    const copyAction = target.closest(".copy-btn");

    if (copyAction) {
      if (timer.current) {
        return;
      }

      const copyCode = () => {
        const textarea = document.createElement("textarea");
        textarea.value = decodeURIComponent(copyAction.getAttribute("value") || "");
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      };

      copyAction.classList.add("copied");
      copyCode();
      timer.current = setTimeout(() => {
        copyAction.classList.remove("copied");
        timer.current = null;
      }, 1500);
    }
  };

  const getExtensionName = (url: string) => {
    const name = url.split(".").pop();
    return name ? name.toLowerCase() : "";
  };

  const attachments = (record: ShareRecord) => {
    if (record.attachments) {
      try {
        const attachments = JSON.parse(record.attachments) as { fileName: string; fileId: string }[];
        return attachments
          .map((item, index) => {
            const extensionName = getExtensionName(item.fileName);
            const fileUrl = `${process.env.OSS_URL}/file/${item.fileName}`;
            const icon = fileIcon[extensionName as keyof typeof fileIcon];

            if (!icon) {
              return null;
            }

            return (
              <div
                key={index}
                className="inline-flex gap-4 items-center p-[0.63rem] shadow-[0_0_2.5rem_0_#0000000f] transition-all dark:bg-[#1E1E1E] rounded-[0.56rem] cursor-pointer"
                onClick={() => {
                  window.open(fileUrl, "_blank");
                }}
              >
                {icon}
                <div className="flex-1 overflow-hidden">
                  <div className="font-roboto text-[12px] font-[700] truncate w-full">{item.fileName}</div>
                  <div className="font-roboto text-[12px] text-[#cbd5e1] dark:text-[#727272] font-[700] leading-[1.2]">
                    {extensionName.toUpperCase()}
                  </div>
                </div>
              </div>
            );
          })
          .filter(Boolean);
      } catch (error) {
        console.log(error);
      }
    }
    return null;
  };

  useEffect(() => {
    const maxTime = 100;
    let currentTime = 0;

    const observer = new MutationObserver(async (mutations, obs) => {
      if (currentTime > maxTime) {
        obs.disconnect();
        console.error("render mermaid timeout");
        return;
      }

      const action = async () => {
        currentTime++;
        console.debug("render mermaid", currentTime);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const renderMermaidElements = document.querySelectorAll(".render-mermaid:not(.rendered)");
        if (renderMermaidElements.length === 0) {
          return;
        }
        renderMermaidElements.forEach((el) => {
          const isInDocument = el.ownerDocument === document;
          if (isInDocument) {
            const target = el as HTMLElement;
            target.click();
            target.classList.remove("loading");
          }
        });
        await action();
      };

      await action();
      obs.disconnect();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="dark:text-white max-w-[52.5rem] mx-auto pt-[60px] px-[22px]">
      {records
        .sort((a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime())
        .map((record, i) => {
          const answer = mendMarkdown(record.answer)
            .replace(/```thinking/g, "<juchats-thinking>")
            .replace(/```(?=[^`]*<juchats-thinking>)/g, "</juchats-thinking>");

          return (
            <div key={i}>
              <div className={style.record()}>
                <PerfectCorner src={user.avatar} />
                <div className={style.content()}>
                  <span className={style.profile()}>{user.name}</span>
                  <span className={style.model()}>{record.modelShowName}</span>
                  <div className="whitespace-pre-wrap break-all">
                    {renderQuestion(record.question, record.originalUrls || [])}
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-3 gap-4 mt-4 flex-col">{attachments(record)}</div>
                </div>
              </div>
              <div className={style.record()}>
                <PerfectCorner src="/herm-chat/logo-dark.svg" padding={20} />
                <div className={style.content()} onClick={onClick}>
                  <span className={style.profile()}>{ProductName}</span>
                  <span className={style.model()}>{record.modelShowName}</span>
                  <RenderAnswer
                    artifacts={record.artifactsList}
                    answer={answer}
                    modelId={record.modeId}
                    originalUrls={record.originalUrls || []}
                    relationAnswers={
                      record.relationAnswers
                        ? record.relationAnswers.sort(
                            (a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime()
                          )
                        : []
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};
